import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useIntegratorContext } from '../context/IntegratorContext';
import { integratorApi } from '../api/integrators';
import { Integrator } from '../types';
import NotificationBell from './NotificationBell';
import { NavIcon, iconForPath } from './icons';
import BrandName from './BrandName';
import './Sidebar.css';

type NavItem = { path: string; label: string };
type NavGroup = { heading: string; items: NavItem[] };

/** Globalny kontekst integratora (tylko super-admin): autocomplete po NIP/nazwie. */
const IntegratorContextPicker: React.FC = () => {
  const { integrator, setIntegrator } = useIntegratorContext();
  const [all, setAll] = useState<Integrator[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    integratorApi.list().then(({ data }) => setAll(data)).catch(() => {});
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q
      ? all.filter((i) => (i.nip || '').toLowerCase().startsWith(q) || i.name.toLowerCase().includes(q))
      : all;
    return pool.slice(0, 8);
  }, [query, all]);

  if (integrator) {
    return (
      <div className="sidebar-group" style={{ padding: '0 0.75rem' }}>
        <div className="sidebar-group-heading">Kontekst integratora</div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.375rem 0.5rem', borderRadius: 'var(--radius)',
          background: 'var(--color-surface-alt)', fontSize: '0.8125rem',
        }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <strong>{integrator.name}</strong>
            {integrator.nip && <span className="mono" style={{ display: 'block' }}>NIP {integrator.nip}</span>}
          </span>
          <button
            type="button"
            title="Wyjdź z kontekstu integratora"
            onClick={() => setIntegrator(null)}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar-group" style={{ padding: '0 0.75rem', position: 'relative' }}>
      <div className="sidebar-group-heading">Kontekst integratora</div>
      <input
        className="form-input"
        style={{ width: '100%', fontSize: '0.8125rem' }}
        value={query}
        placeholder="NIP lub nazwa firmy…"
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: '0.75rem', right: '0.75rem', zIndex: 50,
          background: 'var(--color-surface)', border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          maxHeight: 240, overflowY: 'auto',
        }}>
          {suggestions.map((i) => (
            <button
              type="button"
              key={i.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setIntegrator({ id: i.id, name: i.name, nip: i.nip })}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '0.5rem 0.625rem', border: 'none', background: 'transparent',
                cursor: 'pointer', fontSize: '0.8125rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-alt)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {i.name}
              <span className="mono" style={{ display: 'block', color: 'var(--color-text-muted)' }}>
                {i.nip ? `NIP ${i.nip}` : 'brak NIP'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.roles?.includes('ADMIN');
  const isCustomer = user?.roles?.includes('CUSTOMER');
  const isEmployeeAdmin = user?.roles?.includes('EMPLOYEE_ADMIN');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const groups: NavGroup[] = isAdmin
    ? [
        {
          heading: 'Operacje',
          items: [
            { path: '/machines', label: 'Maszyny' },
            { path: '/customers', label: 'Klienci' },
            { path: '/documentation', label: 'Dokumentacja' },
          ],
        },
        {
          heading: 'Zgłoszenia',
          items: [
            { path: '/my-tickets', label: 'Zgłoszenia' },
            { path: '/admin/assignments', label: 'Przypisania' },
            { path: '/notes', label: 'Notatki' },
          ],
        },
        {
          heading: 'Administracja',
          items: [
            { path: '/admin/integrators', label: 'Integratorzy' },
            { path: '/admin/billing', label: 'Plany i faktury' },
            { path: '/admin/users', label: 'Użytkownicy' },
            { path: '/admin/stats', label: 'Statystyki' },
            { path: '/admin/ai-costs', label: 'Koszty AI' },
            { path: '/admin/indexing', label: 'Indeksacje' },
            { path: '/admin/settings', label: 'Ustawienia' },
            { path: '/admin/gdpr', label: 'RODO' },
          ],
        },
      ]
    : isCustomer
      ? [
          {
            heading: 'Konto',
            items: [
              { path: '/machines', label: 'Maszyny' },
              { path: '/documentation', label: 'Dokumentacja' },
              { path: '/company-account', label: 'Konto firmy' },
              { path: '/profile', label: 'Twoje konto' },
            ],
          },
        ]
      : isEmployeeAdmin
        ? [
            {
              heading: 'Praca',
              items: [
                { path: '/machines', label: 'Maszyny' },
                { path: '/customers', label: 'Klienci' },
                { path: '/documentation', label: 'Dokumentacja' },
                { path: '/my-tickets', label: 'Zgłoszenia' },
                { path: '/notes', label: 'Notatki' },
              ],
            },
            {
              heading: 'Zespół',
              items: [
                { path: '/team/members', label: 'Pracownicy' },
                { path: '/team/invitations', label: 'Zaproszenia' },
                { path: '/team/subscription', label: 'Abonament' },
                { path: '/admin/assignments', label: 'Przypisania' },
                { path: '/admin/stats', label: 'Statystyki' },
              ],
            },
          ]
        : [
            {
              heading: 'Praca',
              items: [
                { path: '/machines', label: 'Maszyny' },
                { path: '/documentation', label: 'Dokumentacja' },
                { path: '/my-tickets', label: 'Zgłoszenia' },
                { path: '/notes', label: 'Notatki' },
              ],
            },
          ];

  const isActive = (path: string) => location.pathname.startsWith(path);

  const roleLabel = isAdmin ? 'Administrator' : isCustomer ? 'Klient' : isEmployeeAdmin ? 'Admin integratora' : 'Integrator';

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <Link to="/machines" className="sidebar-logo"><BrandName /></Link>
        {user && (
          <div className="notification-bell-wrap">
            <NotificationBell />
          </div>
        )}
      </div>

      {isAdmin && <IntegratorContextPicker />}

      <nav className="sidebar-nav">
        {groups.map((g) => (
          <div key={g.heading} className="sidebar-group">
            <div className="sidebar-group-heading">{g.heading}</div>
            {g.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              >
                <NavIcon name={iconForPath(item.path)} size={15} className="sidebar-link-icon" />
                {item.label}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <Link to="/profile" className="sidebar-user-link">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="Avatar" className="sidebar-avatar" />
          ) : (
            <span className="sidebar-avatar-fallback">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </span>
          )}
          <span className="sidebar-user-meta">
            <span className="sidebar-user-name">{user?.firstName} {user?.lastName}</span>
            <span className="sidebar-user-role">{roleLabel}</span>
          </span>
        </Link>
        {(user?.companyName || user?.companyNip) && (
          <Link to="/company-account" className="sidebar-company"
            title="Konto firmy"
            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            {user.companyLogoUrl ? (
              <img src={user.companyLogoUrl} alt="Logo firmy" className="sidebar-company-logo" />
            ) : (
              <span className="sidebar-company-logo-fallback">
                {(user.companyName || user.companyNip || '?')[0].toUpperCase()}
              </span>
            )}
            <span className="sidebar-company-meta">
              <span className="sidebar-company-name">{user.companyName || `NIP ${user.companyNip}`}</span>
              {user.companyName && user.companyNip && (
                <span className="sidebar-company-nip">NIP {user.companyNip}</span>
              )}
            </span>
          </Link>
        )}
        <Link to="/regulamin" className="sidebar-link">
          <NavIcon name="docs" size={15} className="sidebar-link-icon" />
          Regulamin
        </Link>
        <Link to="/polityka-prywatnosci" className="sidebar-link">
          <NavIcon name="shield" size={15} className="sidebar-link-icon" />
          Polityka prywatności
        </Link>
        <Link to="/change-password" className="sidebar-link">
          <NavIcon name="key" size={15} className="sidebar-link-icon" />
          Zmień hasło
        </Link>
        <button onClick={handleLogout} className="sidebar-logout">
          <NavIcon name="logout" size={15} className="sidebar-link-icon" />
          Wyloguj
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
